import { useEffect, useState } from "react";
import { cn, ModeToggle, Separator, SidebarTrigger } from "@/components/kit";
import { ProfileDropdown } from "@/modules/auth/presentation/ui/ProfileDropdown";
import { Breadcrumbs } from "./Breadcrumbs";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";

type HeaderProps = React.HTMLAttributes<HTMLElement> & {
  fixed?: boolean;
  ref?: React.Ref<HTMLElement>;
};

export function Header({ className, fixed, children, ...props }: HeaderProps) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setOffset(document.body.scrollTop || document.documentElement.scrollTop);
    };

    document.addEventListener("scroll", onScroll, { passive: true });

    return () => document.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "z-50 h-16 border-b border-secondary/15 bg-background/90 backdrop-blur-md",
        fixed && "header-fixed peer/header sticky top-0 w-[inherit]",
        offset > 10 && fixed && "shadow-sm shadow-secondary/10",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "relative flex h-full items-center gap-3 px-4 sm:gap-4",
          offset > 10 &&
            fixed &&
            "after:absolute after:inset-0 after:-z-10 after:bg-background/80 after:backdrop-blur-lg",
        )}
      >
        <SidebarTrigger
          variant="outline"
          className="max-md:scale-125 border-secondary/30"
        />
        <Separator orientation="vertical" className="h-6 bg-secondary/25" />
        {children ?? <Breadcrumbs />}
        <div className="ms-auto flex items-center gap-2 sm:gap-3">
          <GlobalSearch />
          <NotificationBell />
          <ModeToggle />
          <ProfileDropdown />
        </div>
      </div>
    </header>
  );
}
